import { useTranslation } from 'react-i18next';
import React, { useCallback, memo, useMemo } from 'react';
import cx from 'clsx';

import {
  IconRcFollow,
  IconRcFollowingDark,
  IconRcFollowingLight,
  IconRcUnFollow,
} from '@/assets/follow';
import { useOfficialAccount, useUpdateOfficialFollow } from '@/hooks/useOfficialAccount';
import { useDeviceTypeIsMobile, useTheme } from '@/hooks';

import { moreFeatureModalTip } from '../shared/MobileModalTip';
import { HelperTooltip } from '../HelperTooltip';
import { Button } from '../Button';
import style from './index.module.scss';

export interface OfficialAccountFollowBtnProps {
  className?: string;
  officialId: string;
  restParams?: Record<string, string | number | boolean | undefined>;
  followCallback?: (isFollowing: boolean) => void;
  isIconType?: boolean;
}

export const OfficialAccountFollowBtn = memo(
  ({
    className,
    officialId,
    followCallback,
    restParams,
    isIconType,
  }: OfficialAccountFollowBtnProps) => {
    const { t } = useTranslation();
    const { isDark } = useTheme();
    const { officialAccountDetail, isLoading } = useOfficialAccount(officialId);

    const { updateFollow, isLoading: followLoading } = useUpdateOfficialFollow(
      officialId,
      followCallback,
    );
    const isFollowing = officialAccountDetail?.is_following ?? false;
    const isMobile = useDeviceTypeIsMobile();
    const handleFollowClick = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        e.preventDefault();
        if (isMobile) {
          moreFeatureModalTip();
          return;
        }
        updateFollow(restParams);
      },
      [isMobile, restParams, updateFollow],
    );
    const IconRcFollowing = useMemo(
      () => (isDark ? IconRcFollowingDark : IconRcFollowingLight),
      [isDark],
    );

    return !isLoading ? (
      <Button
        className={cx(
          style.officialAccountFollowBtn,
          isFollowing ? style.followingBtn : style.toFollowBtn,
          className,
        )}
        type={isIconType ? 'text' : isFollowing ? 'default' : 'primary'}
        loading={followLoading}
        ghost
        onClick={handleFollowClick}>
        {isFollowing ? (
          <>
            <HelperTooltip disable={!isIconType} title={t('FollowButton.follow')} placement="top">
              <span className={style.follwing}>
                <div className={style.followBtnInner}>
                  <IconRcFollowing className={style.icon} />
                  {isIconType ? null : t('OfficialAccountFollowBtn.19t47zl')}
                </div>
              </span>
            </HelperTooltip>
            <HelperTooltip disable={!isIconType} title={t('FollowButton.1trw9m2')} placement="top">
              <span className={style.unfollow}>
                <div className={style.followBtnInner}>
                  <IconRcUnFollow className={style.icon} />
                  {isIconType ? null : t('OfficialAccountFollowBtn.1trw9m2')}
                </div>
              </span>
            </HelperTooltip>
          </>
        ) : (
          <HelperTooltip disable={!isIconType} title={t('FollowButton.follow')} placement="top">
            <div className={style.followBtnInner}>
              <IconRcFollow className={cx(style.icon, style.colorPrimary)} />
              {isIconType ? null : t('FollowButton.follow')}
            </div>
          </HelperTooltip>
        )}
      </Button>
    ) : null;
  },
);
